import * as angular from "angular";
import { IMapRegularShape } from "../layers/types";
import { TransformInteraction } from "./transformInteraction";
import { MapRegularShapeType as RegularShapeType } from "../generated/enums";
import { ol_Coordinate, ol_Feature, ol_Map, ol_MapBrowserEvent, ol_geom } from "@omnia/exports/openlayers";


export interface IRegularShapeTransform {
    translation: number[];
    rotation: number;
    scale: number[];
}

export class RegularShape {

    private _type: RegularShapeType;

    private _center: ol_Coordinate;

    private _size1: number;
    private _size2: number;

    private _angle: number;

    get type(): RegularShapeType {
        return this._type;
    }

    get center(): ol_Coordinate {
        return this._center;
    }

    get width(): number {
        return this._size1;
    }

    get height(): number {
        return this._size2;
    }

    get angle(): number {
        return this._angle;
    }

    static setupCircle(center: ol_Coordinate, radius: number) {
        let s = new RegularShape();
        s._type = RegularShapeType.Circle;
        s._center = center;
        s._size1 = radius;
        return s;
    }

    static setupRectangle(center: ol_Coordinate, width: number, height: number, angle: number) {
        let s = new RegularShape();
        s._type = RegularShapeType.Rectangle;
        s._center = center;
        s._size1 = width;
        s._size2 = height;
        s._angle = angle;
        return s;
    }

    static setupSquare(center: ol_Coordinate, side: number, angle: number): RegularShape {
        let s = new RegularShape();
        s._type = RegularShapeType.Square;
        s._center = center;
        s._size1 = side;
        s._size2 = side;
        s._angle = angle;
        return s;
    }

    static create(def: IMapRegularShape) {

        switch (def.Type) {

            case RegularShapeType.Circle:
                return RegularShape.setupCircle(def.Center, def.Size1);

            case RegularShapeType.Rectangle:
                return RegularShape.setupRectangle(def.Center, def.Size1, def.Size2, def.Angle);

            case RegularShapeType.Square:
                return RegularShape.setupSquare(def.Center, def.Size1, def.Angle);

        }

    }

    createGeometry(): ol_geom.Geometry {

        let center = this._center;
        let angle = this._angle;
        let size1 = this._size1;
        let size2 = this._size2;

        switch (this._type) {

            case RegularShapeType.Circle:
                return new (<any>ol_geom.Circle)(center, size1);

            case RegularShapeType.Rectangle:
                return RegularShape.createRectangle(center, size1, size2, angle);

            case RegularShapeType.Square:
                return RegularShape.createRectangle(center, size1, size1, angle);

        }

    }

    static createRectangle(center: ol_Coordinate, width: number, height: number, angle: number): ol_geom.Geometry {

        let x = center[0];
        let y = center[1];
        let w2 = width / 2;
        let h2 = height / 2;

        let coords = [
            [x - w2, y - h2],
            [x + w2, y - h2],
            [x + w2, y + h2],
            [x - w2, y + h2],
            [x - w2, y - h2],
        ];

        let rect = new ol_geom.Polygon([coords]);

        if (angle) {
            (<any>rect).rotate(angle, center);
        }

        return rect;

    }

    public transform(transform: IRegularShapeTransform): RegularShape {

        let center = this._center;
        let angle = this._angle || 0;
        let size1 = this._size1;
        let size2 = this._size2;

        let t = transform;

        if (t && t.translation) {
            center = [center[0] + t.translation[0], center[1] + t.translation[1]];
        }

        if (t && t.rotation) {
            angle += t.rotation;
        }

        if (t && t.scale) {
            size1 *= t.scale[0];
            size2 *= t.scale[1];
        }

        let shape = new RegularShape();


        shape._type = this._type;
        shape._center = center;
        shape._size1 = size1;
        shape._size2 = size2;
        shape._angle = angle;

        return shape;

    }

    asSerializable() {

        return {
            Type: this._type,
            Center: <[number, number]>this._center,
            Size1: this._size1,
            Size2: this._size2,
            Angle: (this._angle ? this._angle : undefined),
        };

    }

}

export class RegularShapeFeature {


    private _feature: ol_Feature;
    private _shape: RegularShape;

    constructor() {
        this._feature = new ol_Feature();
        this._shape = new RegularShape();
    }

    clone(): RegularShapeFeature {

        let clone = new RegularShapeFeature();

        clone._shape = angular.copy(this._shape, new RegularShape());
        clone._feature = new ol_Feature();
        clone.updateGeometry();

        return clone;

    }

    get feature(): ol_Feature {
        return this._feature;
    }

    set feature(value: ol_Feature) {
        this._feature = value;
        this.updateGeometry();
    }

    get type(): RegularShapeType {
        return this._shape.type;
    }

    get center(): number[] {
        return this._shape.center;
    }

    get width(): number {
        return this._shape.width;
    }

    get height(): number {
        return this._shape.height;
    }

    get angle(): number {
        return this._shape.angle;
    }

    public setupCircle(center: ol_Coordinate, radius: number) {
        this._shape = RegularShape.setupCircle(center, radius);
        this.updateGeometry();
    }

    public setupRectangle(center: ol_Coordinate, width: number, height: number, angle: number) {
        this._shape = RegularShape.setupRectangle(center, width, height, angle);
        this.updateGeometry();
    }

    public setupSquare(center: ol_Coordinate, side: number, angle: number) {
        this._shape = RegularShape.setupSquare(center, side, angle);
        this.updateGeometry();
    }

    get shapeDefinition() {
        return this._shape;
    }

    hasChanged(shape: RegularShape) {
        if (!shape) return false;
        // TODO
        return (shape !== this._shape);
    }

    public setShape(shape: RegularShape) {
        this._shape = shape;
        this.updateGeometry();
    }

    public setTransform(transform: IRegularShapeTransform): RegularShape {

        let shape = this._shape.transform(transform);
        this.updateGeometry(shape);

        return shape;

    }

    public updateGeometry(shape?: RegularShape) {

        shape = shape || this._shape;
        if (!shape) return;

        let geom = shape.createGeometry();

        if (geom) {
            this._feature.setGeometry(geom);
        }

    }

}


export class RegularShapeEditInteraction extends TransformInteraction {

    private _shape: RegularShapeFeature;
    private _transformedShape: RegularShape;

    private _rectangle: boolean;
    private _resizeDimension: boolean;


    public setFeature(feature: ol_Feature) {

        this._shape = null;
        this._rectangle = false;
        super.setFeature(feature);

    }

    public setShape(shape: RegularShapeFeature) {

        this._shape = shape;
        this._rectangle = false;

        if (!shape) {
            super.setFeature(null);
        }
        else {
            this._rectangle = (shape.type == RegularShapeType.Rectangle);
            super.setFeature(shape.feature, true);
        }

    }

    protected commitCurrentTransform() {

        if (this._transformedShape) {
            this._shape.setShape(this._transformedShape);
            this._transformedShape = null;
        }

        super.commitCurrentTransform();

    }

    protected transform(translation: number[], scale: number, rotationAngle: number, dragCoord: ol_Coordinate) {

        if (!this._shape) {
            return super.transform(translation, scale, rotationAngle, dragCoord);
        }

        let scaleX: number;
        let scaleY: number;

        if (scale) {

            let dimension = this._resizeDimension;

            if (dimension === true) {
                scaleX = 1;
                scaleY = scale;
            }
            else if (dimension === false) {
                scaleX = scale;
                scaleY = 1;
            }
            else {
                scaleX = scaleY = scale;
            }

        }

        let transform: IRegularShapeTransform = {
            translation: translation,
            rotation: rotationAngle,
            scale: (scaleX ? [scaleX, scaleY] : null),
        };

        this._transformedShape = this._shape.setTransform(transform);

        this.transformOverlays(translation, scaleX, scaleY, rotationAngle);

    }

    protected transformOverlays(translation: number[], scaleX: number, scaleY: number, rotationAngle: number) {

        let type = this._shape ? this._shape.type : null;

        switch (type) {

            case RegularShapeType.Square:
            case RegularShapeType.Rectangle:
                let p = this.getDefaultOverlayPosition();
                this._rotationOverlay.overlay.setPosition(p);
                break;

            case RegularShapeType.Circle:
                break;

            default:
                super.transformOverlays(translation, scaleX, scaleY, rotationAngle);
                break;

        }

    }

    protected getDefaultOverlayPosition(): ol_Coordinate {

        let type = this._shape ? this._shape.type : null;

        switch (type) {

            case RegularShapeType.Square:
            case RegularShapeType.Rectangle:

                let g = <ol_geom.Polygon>(this._feature ? this._feature.getGeometry() : null);

                if (g) {
                    return g.getCoordinates(true)[0][1];
                }

            case RegularShapeType.Circle:
                return undefined;

        }

        return super.getDefaultOverlayPosition();

    }

    protected handleDownEvent(evt: ol_MapBrowserEvent): boolean {

        this._resizeDimension = null;
        this._transformedShape = null;

        let r = super.handleDownEvent(evt);

        if (r && this._rectangle && this._drag.resize && (!this._drag.rotate)) {

            this.setupResizeDragOp(evt.map);

        }

        return r;

    }

    private setupResizeDragOp(map: ol_Map) {

        let origin = this._drag.origin;
        let center: number[];

        let dir = this.getResizeDirection(map, origin);
        this._resizeDimension = !(dir % 2);

        switch (dir) {

            case 0:
            case 2:
                {
                    let sign = (dir == 2 ? -1 : 1);
                    let h2 = this._shape.height / 2;
                    let angle = (Math.PI / 2) - this._shape.angle;

                    center = [
                        origin[0] + (sign * h2 * Math.cos(angle)),
                        origin[1] + (sign * h2 * Math.sin(angle)),
                    ];
                }
                break;

            case 1:
            case 3:
                {
                    let sign = (dir == 1 ? -1 : 1);
                    let w2 = this._shape.width / 2;
                    let angle = this._shape.angle;

                    center = [
                        origin[0] + (sign * w2 * Math.cos(angle)),
                        origin[1] + (sign * w2 * Math.sin(angle)),
                    ];
                }
                break;

            default:
                return;

        }

        this._drag.center = center;

        this._drag.initDistance = this.getDistance(center, origin);


    }

    private getResizeDirection(map: ol_Map, origin: ol_Coordinate): number {

        origin = map.getPixelFromCoordinate(origin);

        let coords = (<ol_geom.Polygon>this._feature.getGeometry()).getCoordinates()[0]
            .map(c => map.getPixelFromCoordinate(c));

        let best: number = Number.MAX_VALUE;
        let bestIndex: number;

        for (let i = 0; i < 4; i++) {

            let dist: number;

            let p1 = coords[i];
            let p2 = coords[i + 1];

            dist = this.getDistanceSquared(p1, origin);
            if (dist <= 100) return null;

            dist = this.getDistanceSquared(p2, origin);
            if (dist <= 100) return null;

            dist = this.getDistanceSquared(this.getClosestPointOnLine(origin, p1, p2), origin);

            if (dist < best) {
                best = dist;
                bestIndex = i;
            }

        }


        return bestIndex;

    }


}

